import type { CSSProperties, ReactNode } from "react";

type RevealOnScrollProps = {
  children: ReactNode;
  className?: string;
  /**
   * Stagger, in ms, relative to sibling reveals. Converted to a small offset on the
   * scroll-driven animation range rather than a timer, so siblings still resolve in
   * order without any JavaScript.
   */
  delayMs?: number;
  /**
   * Kept for call-site compatibility. Both variants now use the same one-way reveal:
   * the old opacity-tracks-scroll behaviour left partly-visible sections permanently
   * dimmed, which read as blank page.
   */
  fadeWithScroll?: boolean;
  /**
   * The element to render. Defaults to a div.
   *
   * `li` exists because six call sites wrapped an `<li>` in this component and so put a
   * `<div>` directly inside a `<ul>`/`<ol>`. Browsers paint it, but a list whose children
   * are not list items stops being a list to a screen reader, which announces neither the
   * item count nor the position. Rendering the `li` here keeps the reveal and the
   * semantics in the same element.
   */
  as?: "div" | "li";
  /** Anchor target, for the `/built` deep links that point at a single item. */
  id?: string;
};

/** ms of stagger per 1% of extra entry range. Keeps the offset small enough that a
 * staggered sibling still finishes revealing well before it reaches mid-viewport. */
const MS_PER_PERCENT = 40;
const MAX_OFFSET_PERCENT = 12;

/**
 * Fades a block in as it scrolls into view.
 *
 * This is a server component on purpose. It used to be a client component driving
 * opacity from an IntersectionObserver, which meant every revealed block rendered at
 * opacity 0 and depended on JavaScript to ever become visible. When a CSP change
 * stopped the page hydrating, that turned most of the home page into blank space with
 * no error anywhere. The reveal is now pure CSS (see `.ownly-reveal-scroll` in
 * globals.css) and degrades to plain visible content wherever it is not supported.
 */
export function RevealOnScroll({
  children,
  className = "",
  delayMs = 0,
  as: Tag = "div",
  id,
}: RevealOnScrollProps) {
  const offsetPercent = Math.min(
    MAX_OFFSET_PERCENT,
    Math.round(delayMs / MS_PER_PERCENT),
  );

  const style =
    offsetPercent > 0
      ? ({ "--ownly-reveal-offset": `${offsetPercent}%` } as CSSProperties)
      : undefined;

  return (
    <Tag id={id} style={style} className={`ownly-reveal-scroll ${className}`}>
      {children}
    </Tag>
  );
}
